import React, {useEffect, useRef, useMemo} from 'react';
import {useThree} from '@react-three/fiber';
import * as THREE from 'three';
import {Line2} from 'three/examples/jsm/lines/Line2';
import {LineMaterial} from 'three/examples/jsm/lines/LineMaterial';
import {LineGeometry} from 'three/examples/jsm/lines/LineGeometry';
import {useAppSelector} from '../../app/useAppSelector';

interface TrailData {
  assetId: string;
  points: Array<{x: number; y: number; z: number; timestamp: number}>;
  color: string;
}

export const TrailRenderer: React.FC = () => {
  const {scene, size} = useThree();

  // Redux selectors replace all eventBus subscriptions
  const showTrails = useAppSelector(s => s.rtls.showTrails);
  const positionHistory = useAppSelector(s => s.rtls.positionHistory);
  const assetsRecord = useAppSelector(s => s.rtls.assets);
  const playbackMode = useAppSelector(s => s.rtls.playbackMode);
  const selectedAssetId = useAppSelector(s => s.rtls.selectedAssetId);
  const panelFilter = useAppSelector(s => s.rtls.panelFilter);
  const isolatingPlayback = playbackMode === 'playback' && selectedAssetId != null;

  const groupRef = useRef<THREE.Group>(new THREE.Group());

  // Build trailData from Redux positionHistory + assets. Gate by panelFilter
  // so trails mirror the visible markers: 'all' shows both types,
  // 'equipment'/'personnel' shows only that type, and any non-asset filter
  // (location/access_point/chamber) shows none.
  const trailData = useMemo<TrailData[]>(() => {
    const typeMatch =
      panelFilter === 'equipment' || panelFilter === 'personnel'
        ? panelFilter
        : null;
    const showAny = panelFilter === 'all' || typeMatch !== null;
    if (!showAny) return [];
    return Object.entries(positionHistory)
      .map(([assetId, entries]) => {
        const asset = assetsRecord[assetId];
        if (!asset) return null;
        if (typeMatch !== null && asset.type !== typeMatch) return null;
        const color = asset.type === 'equipment' ? '#3B82F6' : '#F97316';
        return {
          assetId,
          points: entries.map(e => ({...e.position, timestamp: e.timestamp})),
          color,
        };
      })
      .filter((t): t is NonNullable<typeof t> => t !== null && t.points.length >= 2);
  }, [positionHistory, assetsRecord, panelFilter]);

  useEffect(() => {
    const group = groupRef.current;
    scene.add(group);
    return () => {
      scene.remove(group);
      disposeGroup(group);
    };
  }, [scene]);

  // Build trail meshes whenever trails, visibility, or canvas size changes
  useEffect(() => {
    const group = groupRef.current;
    disposeGroup(group);

    if (!showTrails || trailData.length === 0) {
      return;
    }

    // Always draw the FULL trail — the trail is a route preview, and the
    // asset marker traces along it during playback. Clipping by
    // playbackTimestamp (the old M3.5 behavior) made the line draw as the
    // asset moved, which inverts the intended UX.

    for (const trail of trailData) {
      if (!trail.points || trail.points.length < 2) continue;
      // Playback isolation: only render the selected asset's trail.
      if (isolatingPlayback && trail.assetId !== selectedAssetId) continue;

      // Build flat position array [x,y,z, x,y,z, ...] from all trail points.
      const positions: number[] = [];
      for (const p of trail.points) {
        positions.push(p.x, p.y, p.z);
      }

      // Need at least 2 points (6 floats) to render a line
      if (positions.length < 6) continue;

      // Line2 with LineMaterial gives consistent-width lines (not 1px WebGL lines)
      const geometry = new LineGeometry();
      geometry.setPositions(positions);

      const material = new LineMaterial({
        color: new THREE.Color(trail.color).getHex(),
        linewidth: 3, // pixels
        transparent: true,
        opacity: 0.9,
        depthWrite: false,
        resolution: new THREE.Vector2(size.width, size.height),
      });
      material.toneMapped = false;

      const line = new Line2(geometry, material);
      line.computeLineDistances();
      line.renderOrder = 10;
      line.frustumCulled = false;
      group.add(line);
    }
  }, [trailData, showTrails, size, isolatingPlayback, selectedAssetId]);

  return null;
};

TrailRenderer.displayName = 'TrailRenderer';

function disposeGroup(group: THREE.Group) {
  while (group.children.length > 0) {
    const child = group.children[0];
    group.remove(child);
    child.traverse((obj: THREE.Object3D) => {
      if ((obj as any).geometry) {
        (obj as any).geometry.dispose();
      }
      if ((obj as any).material) {
        const mat = (obj as any).material;
        if (Array.isArray(mat)) {
          mat.forEach((m: any) => m.dispose());
        } else {
          mat.dispose();
        }
      }
    });
  }
}
